import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { listDunnings } from '../../../../services/finance/dunningApi';
import { financeErrorMessage } from '../../utils/apiError';
import { LIST_PER_PAGE } from '../constants';
import { daysBetween } from '../../utils/dunningFormat';

// ---------------------------------------------------------------------------
// Lista de casos — UM fetch alimenta o kanban E a fila (trocar de visão não
// refaz request). Sequência guarda corrida: resposta de recorte antigo que
// chega atrasada é descartada.
//
// Sem realtime no backend (webhooks + job 06:00): a lista refaz o fetch ao
// voltar o foco para a janela (mínimo pedido no guia §8.3), em modo silencioso
// (sem skeleton).
// ---------------------------------------------------------------------------

// Recorte de compromisso derivado de `next_action_at` (o backend não filtra
// por isso — contrato §4.1). Semântica igual à pill do card (commitPill).
function matchesCommitFilter(row, commitFilter, today) {
   if (commitFilter === 'all') return true;
   if (commitFilter === 'none') return !row.next_action_at;
   if (!row.next_action_at) return false;
   const late = daysBetween(row.next_action_at, today);
   if (commitFilter === 'late') return late > 0;
   if (commitFilter === 'today') return late === 0;
   return late < 0; // 'ontrack' — data futura
}

export default function useDunningList({ filters, assignee, commitFilter, today }) {
   const [rows, setRows] = useState([]);
   const [stageCounts, setStageCounts] = useState(null);
   const [meta, setMeta] = useState(null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState(null);

   const seqRef = useRef(0);
   const filtersRef = useRef(filters);
   filtersRef.current = filters;

   const fetchList = useCallback(async ({ quiet = false } = {}) => {
      seqRef.current += 1;
      const seq = seqRef.current;

      if (!quiet) {
         setLoading(true);
         setError(null);
      }

      try {
         const result = await listDunnings({
            ...filtersRef.current,
            page: 1,
            perPage: LIST_PER_PAGE,
         });
         if (seqRef.current !== seq) return;
         setRows(result.dunnings);
         setStageCounts(result.stageCounts);
         setMeta(result.meta);
         setLoading(false);
         setError(null);
      } catch (err) {
         if (seqRef.current !== seq) return;
         setLoading(false);
         // Refetch silencioso que falha não derruba a lista já exibida.
         if (!quiet) {
            setError(err);
            toast.error(
               financeErrorMessage(err, 'Falha ao carregar os casos.')
            );
         }
      }
   }, []);

   useEffect(() => {
      fetchList();
   }, [fetchList, filters]);

   useEffect(() => {
      const onFocus = () => fetchList({ quiet: true });
      window.addEventListener('focus', onFocus);
      return () => window.removeEventListener('focus', onFocus);
   }, [fetchList]);

   // Mutações respondem o caso inteiro atualizado — substitui na lista sem
   // esperar refetch (a UI reflete na hora; um refetch silencioso depois
   // reconcilia filtro/ordenação, responsabilidade de quem chamou).
   const patchCase = useCallback((updatedCase) => {
      if (!updatedCase?.id) return;
      setRows((prev) =>
         prev.map((row) => (row.id === updatedCase.id ? updatedCase : row))
      );
   }, []);

   // "Sem responsável" e o recorte de compromisso não existem como filtro no
   // contrato (§4.1) — são pós-filtros sobre a página corrente. Com
   // `total_pages > 1` eles enxergam só esta página (mesma limitação
   // documentada dos KPIs — lacuna §8.1).
   const visibleRows = useMemo(() => {
      let result = rows;
      if (assignee === 'none') {
         result = result.filter((row) => !row.assigned_user);
      }
      if (commitFilter && commitFilter !== 'all') {
         result = result.filter((row) =>
            matchesCommitFilter(row, commitFilter, today)
         );
      }
      return result;
   }, [rows, assignee, commitFilter, today]);

   return {
      rows: visibleRows,
      // Server-side, ANTES dos pós-filtros do cliente (assignee 'none'/
      // commit) — serve pra decidir raia vazia, não pra bater com os cards.
      stageCounts,
      meta,
      loading,
      error,
      refetch: fetchList,
      patchCase,
   };
}
